import { useRef, useState } from "react";
import { useNotification } from "@context/NotificationContext";
import { getDocumentsWithToken } from "@services/azureDataApi";
import type { Attachment } from "@services/bayHireApi";
import { SasTokenPrefix } from "@services/user/types";
import { useTranslation } from "react-i18next";

/**
 * DFT-0027, rewritten by DFT-0050 (AD1/AD3): shared attachment access for Bay Hire.
 *
 * Bay Hire no longer mediates attachment bytes through any backend service (DFT-0012's
 * byte-proxy choice, superseded): every attachment is fetched directly from Azure Blob
 * Storage using a SAS READ token scoped to BAY_HIRE (mirroring every other SAS-consuming
 * module's download path, e.g. useDownloadPortServiceDocument) and turned into a
 * short-lived object URL. This hook is the single place that does that, used by the
 * Request Detail page (download to disk) and by the pre-approval dialog's "View" HOD
 * control (open in a new tab).
 */

/**
 * Display name for an attachment: originalFileName -> documentUrl basename ->
 * attachmentType. The basename fallback exists because older attachments were
 * persisted before originalFileName was captured.
 */
export const getAttachmentFileName = (
  attachment: Pick<Attachment, "attachmentType" | "documentUrl" | "originalFileName">,
): string => {
  if (attachment.originalFileName) return attachment.originalFileName;

  const basename = (attachment.documentUrl ?? "")
    .split("?")[0]
    .split("#")[0]
    .split("/")
    .filter(Boolean)
    .pop();
  if (basename) {
    try {
      return decodeURIComponent(basename);
    } catch {
      return basename;
    }
  }

  return attachment.attachmentType;
};

interface UseDownloadBayHireAttachmentOptions {
  /**
   * Where a failure message is surfaced. Defaults to an error toast (matching
   * useBayHireDetailActions); callers that own an inline error banner (e.g. the
   * pre-approval dialog) pass their own setter instead.
   */
  onError?: (message: string) => void;
  /** Overrides the default failure message. */
  errorMessage?: string;
}

interface UseDownloadBayHireAttachmentResult {
  /** True while the given documentUrl is being fetched (per-attachment state). */
  isBusy: (documentUrl: string) => boolean;
  /**
   * Fetches directly from Azure via a SAS token and saves the blob under fileName.
   * requestId/attachmentId are accepted but unused (kept for call-site compatibility).
   */
  downloadAttachment: (
    requestId: string,
    attachmentId: string,
    documentUrl: string,
    fileName: string,
  ) => Promise<void>;
  /**
   * Fetches directly from Azure via a SAS token and opens the blob in a new tab.
   * requestId/attachmentId are accepted but unused (kept for call-site compatibility).
   */
  openAttachment: (requestId: string, attachmentId: string, documentUrl: string) => Promise<void>;
}

export const useDownloadBayHireAttachment = (
  options: UseDownloadBayHireAttachmentOptions = {},
): UseDownloadBayHireAttachmentResult => {
  const { onError, errorMessage } = options;
  const { t } = useTranslation();
  const { notify } = useNotification();

  const [inFlightUrls, setInFlightUrls] = useState<string[]>([]);
  // Ref mirror of the in-flight set: the click guard must see the latest value
  // synchronously, so a rapid double-click cannot stack two downloads.
  const inFlightRef = useRef<Set<string>>(new Set());

  const isBusy = (documentUrl: string) => inFlightUrls.includes(documentUrl);

  const reportError = (error: unknown) => {
    console.error("Bay Hire attachment access failed:", error);
    const message = errorMessage ?? t("bay-hire.detail.attachment-download-error");
    if (onError) {
      onError(message);
      return;
    }
    notify({
      title: t("common.error", { defaultValue: "Error" }),
      description: message,
      type: "error",
    });
  };

  // Busy-tracking stays keyed by documentUrl (still unique per attachment and already
  // threaded through the file-card components) even though the actual fetch below is
  // keyed by requestId/attachmentId, not documentUrl.
  const withBlob = async (
    documentUrl: string,
    fetchBlob: () => Promise<Blob>,
    consumeBlob: (blob: Blob) => void,
  ) => {
    if (!documentUrl || inFlightRef.current.has(documentUrl)) return;

    inFlightRef.current.add(documentUrl);
    setInFlightUrls((prev) => [...prev, documentUrl]);
    try {
      const blob = await fetchBlob();
      consumeBlob(blob);
    } catch (error) {
      reportError(error);
    } finally {
      inFlightRef.current.delete(documentUrl);
      setInFlightUrls((prev) => prev.filter((url) => url !== documentUrl));
    }
  };

  // requestId/attachmentId are unused now that fetching is by documentUrl directly (SAS-direct,
  // DFT-0050) - kept in the signature so callers (BayHireRequestDetail, usePreApproveDialog) need
  // no change.
  const downloadAttachment = (
    _requestId: string,
    _attachmentId: string,
    documentUrl: string,
    fileName: string,
  ) =>
    withBlob(
      documentUrl,
      () => getDocumentsWithToken(documentUrl, SasTokenPrefix.BAY_HIRE),
      (blob) => {
        const objectUrl = URL.createObjectURL(blob);
        try {
          const anchor = document.createElement("a");
          anchor.href = objectUrl;
          anchor.download = fileName;
          anchor.rel = "noopener";
          document.body.appendChild(anchor);
          anchor.click();
          anchor.remove();
        } finally {
          // The browser has already taken the blob for the download by the time
          // click() returns, so the object URL must not be leaked.
          URL.revokeObjectURL(objectUrl);
        }
      },
    );

  const openAttachment = (_requestId: string, _attachmentId: string, documentUrl: string) =>
    withBlob(
      documentUrl,
      () => getDocumentsWithToken(documentUrl, SasTokenPrefix.BAY_HIRE),
      (blob) => {
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl, "_blank", "noopener,noreferrer");
      },
    );

  return { isBusy, downloadAttachment, openAttachment };
};
